<template>
  <v-card class="class-header" rounded="xl" elevation="2" :style="headerStyle">
    <div class="class-header__bar" :style="barStyle">
      <div class="class-header__bar-overlay"></div>

      <div class="class-header__bar-content">
        <div class="d-flex align-center flex-wrap">
          <v-btn
            icon
            dark
            class="mr-2"
            aria-label="Voltar para turmas"
            @click="$emit('back')"
          >
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>

          <v-avatar :color="theme.primary" size="46" class="mr-3">
            <v-icon dark>{{ theme.heroIcon }}</v-icon>
          </v-avatar>

          <div>
            <div class="class-header__eyebrow">Gerenciar turma</div>
            <div class="text-h5 font-weight-bold white--text">
              Turma {{ classData.cod || "-" }}
            </div>
          </div>
        </div>

        <v-chip small dark :color="statusColor(classData.status)">
          {{ displayStatus(classData.status) }}
        </v-chip>
      </div>
    </div>

    <div class="class-header__meta">
      <div class="class-header__meta-item">
        <v-icon small class="mr-2" :color="theme.primary">mdi-chart-bar</v-icon>
        <span class="class-header__meta-label">Nível</span>
        <span class="class-header__meta-value">{{
          displayLevel(classData.level)
        }}</span>
      </div>

      <div class="class-header__meta-item">
        <v-icon small class="mr-2" :color="theme.primary">
          mdi-account-group-outline
        </v-icon>
        <span class="class-header__meta-label">Formato</span>
        <span class="class-header__meta-value">{{
          displayFormat(classData.format)
        }}</span>
      </div>

      <div class="class-header__meta-item">
        <v-icon small class="mr-2" :color="theme.primary">mdi-refresh</v-icon>
        <span class="class-header__meta-label">Frequência</span>
        <span class="class-header__meta-value">{{
          displayFrequency(classData.frequency)
        }}</span>
      </div>

      <div class="class-header__meta-item">
        <v-icon small class="mr-2" :color="theme.primary">
          mdi-calendar-clock-outline
        </v-icon>
        <span class="class-header__meta-label">Cronograma</span>
        <span class="class-header__meta-value">{{ scheduleSummary }}</span>
      </div>

      <div class="class-header__meta-item">
        <v-icon small class="mr-2" :color="theme.primary">
          mdi-account-multiple-outline
        </v-icon>
        <span class="class-header__meta-label">Alunos ativos</span>
        <span class="class-header__meta-value">{{ membersCount }}</span>
      </div>
    </div>
  </v-card>
</template>

<script>
import {
  displayGroupFormat,
  displayGroupFrequency,
  displayGroupLevel,
  displayGroupStatus,
  formatScheduleSummary,
  groupStatusColor,
} from "@/shared/classPresentation";

export default {
  name: "ClassHeader",

  props: {
    classData: {
      type: Object,
      required: true,
    },
    theme: {
      type: Object,
      required: true,
    },
    membersCount: {
      type: Number,
      default: 0,
    },
  },

  computed: {
    scheduleSummary() {
      return formatScheduleSummary(
        this.classData.weekDays,
        this.classData.hours
      );
    },
    headerStyle() {
      return {
        "--class-primary": this.theme.primary,
        "--class-secondary": this.theme.secondary,
        "--class-surface": this.theme.surface,
        "--class-border": this.theme.border,
      };
    },
    barStyle() {
      const gradient = `linear-gradient(135deg, ${this.theme.primary}E6, ${this.theme.secondary}E6)`;
      return {
        backgroundImage: `${gradient}, url(${this.theme.toolbarImage})`,
      };
    },
  },

  methods: {
    displayLevel(level) {
      return displayGroupLevel(level);
    },
    displayFormat(format) {
      return displayGroupFormat(format);
    },
    displayFrequency(frequency) {
      return displayGroupFrequency(frequency);
    },
    displayStatus(status) {
      return displayGroupStatus(status);
    },
    statusColor(status) {
      return groupStatusColor(status);
    },
  },
};
</script>

<style scoped>
.class-header {
  width: 100%;
  overflow: hidden;
  border: 1px solid var(--class-border);
  box-shadow: 0 25px 60px -45px rgba(15, 23, 42, 0.35);
}

.class-header__bar {
  position: relative;
  min-height: 96px;
  background-position: center;
  background-size: cover;
}

.class-header__bar-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.1),
    rgba(15, 23, 42, 0.32)
  );
}

.class-header__bar-content {
  position: relative;
  z-index: 1;
  min-height: 96px;
  padding: 18px 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.class-header__eyebrow {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.86);
  font-weight: 700;
}

.class-header__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 28px;
  padding: 18px 24px;
  background: linear-gradient(180deg, #ffffff 0%, var(--class-surface) 100%);
}

.class-header__meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.class-header__meta-label {
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  font-weight: 700;
}

.class-header__meta-value {
  font-size: 0.95rem;
  font-weight: 700;
  color: #111827;
}

@media (max-width: 600px) {
  .class-header__bar-content {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
